import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Zap, Bot } from "lucide-react";
import { Logo, inputCls, btnPrimary, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ company_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (isLogin) await login(f.email, f.password); else await register(f);
      nav("/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <Logo />
        <div className="mt-14 max-w-md w-full">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{isLogin ? "Welcome back" : "Create your workspace"}</h1>
          <p className="text-slate-500 mt-2 text-sm">{isLogin ? "Sign in to manage WhatsApp conversations, AI automation and APIs." : "Register your company to connect your own Meta WhatsApp Business Account."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`${mode}-form`}>
            {!isLogin && <>
              <Field label="Company name"><input required data-testid="register-company-input" className={inputCls} value={f.company_name} onChange={set("company_name")} placeholder="Route39 EV Mobility" /></Field>
              <Field label="Your name"><input required data-testid="register-name-input" className={inputCls} value={f.name} onChange={set("name")} /></Field>
            </>}
            <Field label={mode === "login" ? "Mobile number" : "Work email"}><input required type={mode === "login" ? "text" : "email"} inputMode={mode === "login" ? "numeric" : "email"} data-testid={`${mode}-email-input`} className={inputCls} value={f.email} onChange={set("email")} placeholder={mode === "login" ? "Enter 10-digit mobile number" : "you@company.com"} /></Field>
            <Field label="Password" hint={!isLogin ? "Minimum 8 characters" : undefined}><input required type="password" minLength={isLogin ? 1 : 8} data-testid={`${mode}-password-input`} className={inputCls} value={f.password} onChange={set("password")} /></Field>
            {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid={`${mode}-error`}>{error}</div>}
            <button disabled={busy} data-testid={`${mode}-submit-button`} className={`${btnPrimary} w-full h-11`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{isLogin ? "Sign in" : "Create workspace"}</button>
          </form>
          <p className="text-sm text-slate-500 mt-6">
            {isLogin ? <>New to Chat360? <Link to="/register" className="font-semibold text-[#128C7E]" data-testid="go-register-link">Create a workspace</Link></> :
              <>Already have an account? <Link to="/login" className="font-semibold text-[#128C7E]" data-testid="go-login-link">Sign in</Link></>}
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative overflow-hidden bg-[#075E54]">
        <img src={HERO} alt="EV charging" className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#053d37] via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="text-xs uppercase tracking-[0.25em] text-[#25D366] font-bold">Official Meta WhatsApp Cloud API</div>
          <h2 className="font-display text-4xl font-extrabold mt-3 leading-tight max-w-md">Your own WhatsApp messaging stack. No agency in between.</h2>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg text-sm">
            {[[ShieldCheck, "Tenant-isolated & encrypted"], [Bot, "Grounded AI replies"], [Zap, "Developer API & webhooks"]].map(([I, t]) => (
              <div key={t} className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl p-3"><I className="w-4 h-4 text-[#25D366] mb-2" />{t}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
